import Quill, { Parchment } from 'quill/core';
import React from 'react';
import Comment from './Comment';
import Diff from './Diff';
import ReactBlockEmbed from './ReactBlockEmbed';
import Reaction from './Reaction';
import VersionChange from './VersionChange';
import AttachmentUploader, {
  filterValidFiles,
  isAttachmentUploaded,
} from '../../utils/AttachmentUploader';
import { getAssetTokenFromBlot } from '../AssetTokenContext';
import AttachmentContainer from '../attachment/AttachmentContainer';
import findQuillByBlot from '../utils/findQuillByBlot';
import { convertAttachmentsToHtmlList } from './utils/attachmentsHtml';

export interface AttachmentFile {
  source: string;
  name: string;
  size: number;
}

class Attachment extends ReactBlockEmbed {
  static create(value: AttachmentFile[]) {
    return super.create(AttachmentUploader.normalizeToAttachmentFiles(value));
  }

  constructor(
    scroll: Parchment.ScrollBlot,
    domNode: Node,
    value: (AttachmentFile | File)[],
  ) {
    super(scroll, domNode, value);

    this.appendAttachment = this.appendAttachment.bind(this);
    this.setState({
      value: AttachmentUploader.normalizeToAttachmentFiles(value),
      placeholder: false,
    });
    AttachmentUploader.uploadFiles(
      value.filter((file) => file instanceof File),
      this,
    );
  }

  attach() {
    super.attach();
    AttachmentUploader.addActiveAttachmentBlot(this);
  }

  detach() {
    super.detach();
    AttachmentUploader.removeActiveAttachmentBlot(this);
  }

  format(name: string, value: File[]) {
    if (
      name === Comment.attrName ||
      name === Reaction.attrName ||
      name === Diff.attrName ||
      name === VersionChange.attrName
    ) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor) {
        // @ts-expect-error
        this.attributes.attribute(attributor, value);
      }
    } else {
      super.format(name, value);
    }
  }

  formats() {
    return this.attributes.values();
  }

  html() {
    const assetToken = getAssetTokenFromBlot(this);
    const attachments = this.getAttachmentFiles().filter(isAttachmentUploaded);
    const attachmentsHtmlList = convertAttachmentsToHtmlList(
      attachments,
      assetToken,
    );
    return `<div class="${Attachment.className}">${attachmentsHtmlList}</div>`;
  }

  appendAttachment(files: File[]) {
    if (!files.length) return;
    const value = Attachment.value(this.domNode);

    const index = this.offset(this.scroll);
    if (index >= 0) {
      findQuillByBlot(this)?.setSelection(
        index,
        this.length(),
        Quill.sources.USER,
      );
    }

    if (Array.isArray(value)) {
      const validFiles = filterValidFiles(files);
      if (validFiles.length) {
        AttachmentUploader.uploadFiles(validFiles, this);

        const newValue = value.concat(
          AttachmentUploader.normalizeToAttachmentFiles(validFiles),
        );
        this.setValue(newValue);
        this.setState({ value: newValue });
      }
    }
  }

  togglePlaceholder(enabled: boolean) {
    this.setState({ placeholder: enabled });
  }

  cleanUpload(uploadFileId: string, source: string | null | undefined) {
    const value = Attachment.value(this.domNode);
    if (Array.isArray(value)) {
      const targetIndex = value.findIndex(
        (item) => item.source === uploadFileId,
      );
      if (targetIndex !== -1) {
        if (source) {
          value[targetIndex].source = source;
        } else {
          value.splice(targetIndex, 1);
        }
      }
      this.setValue(value);
      this.setState({ value });
    }
  }

  handleDeleteAttachment(source: string) {
    const value = Attachment.value(this.domNode);
    if (Array.isArray(value)) {
      const index = value.findIndex((item) => item.source === source);
      if (index !== -1) {
        value.splice(index, 1);

        const hasVisibleAttachments = value.some(
          (attachment) =>
            isAttachmentUploaded(attachment) ||
            AttachmentUploader.shouldShowUploadingAttachment(attachment),
        );
        if (hasVisibleAttachments) {
          this.setValue(value);
          this.setState({ value });
        } else {
          this.remove();
        }
      }
    }
  }

  isUploading() {
    // @ts-expect-error
    return !this.store.getState().value.every(isAttachmentUploaded);
  }

  // @ts-expect-error
  render(state) {
    return (
      <AttachmentContainer
        blot={this}
        attachments={state.value}
        placeholder={state.placeholder}
        onDeleteAttachment={(source) => this.handleDeleteAttachment(source)}
        isAttachmentUploaded={isAttachmentUploaded}
        shouldShowUploadingAttachment={(attachment) =>
          AttachmentUploader.shouldShowUploadingAttachment(attachment)
        }
      />
    );
  }

  getAttachmentFiles(): AttachmentFile[] {
    // @ts-expect-error
    return this.store.getState().value;
  }

  getVisibleAttachments() {
    return this.getAttachmentFiles().filter(
      (attachment) =>
        isAttachmentUploaded(attachment) ||
        AttachmentUploader.shouldShowUploadingAttachment(attachment),
    );
  }

  getAttachmentArea() {
    return this.domNode.querySelector<HTMLElement>('.attachment-area');
  }

  getReferenceNodeForBounds() {
    return this.getAttachmentArea() ?? this.domNode;
  }
}

Attachment.blotName = 'attachment';
Attachment.className = 'ql-attachment';
Attachment.tagName = 'div';

export default Attachment;
